import { useMemo } from 'react'
import { IBaseClockingItem, IEmployee } from '@cbgms/api'
import { IClockingAction } from '@cbgms/api/modules/clocking-actions'
import { Icon, Button as SUIButton, Table } from 'semantic-ui-react'
import Timer from './Timer'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReducerState } from 'state/rootReducer'
import { currentClockingAction, workorderClockingActions } from './state/actions'
import { useQuery } from '@tanstack/react-query'
import { clockResponseToTimePerEmployee } from 'app/workorder/components/workorder/calculate-per-employee-time'
import { apiFormat } from '@cbgms/base/utils/datetime'
import { Loader } from '@carsys/ui'
import { Button, Tooltip, Typography } from '@mui/material'
import StopIcon from '@mui/icons-material/Stop'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { useFeature } from '@cbgms/base/feature-flags'

interface ITimeActionDetail {
  clockIn: (typeUUID: string) => void
  clockOut: () => void
  item: IBaseClockingItem
  NameComponent?: React.FC<{ name: string; url?: string; brand?: string }>
  currentAction: IClockingAction | null
  id: string
}

const TimeActionDetails = ({ clockOut, clockIn, item, NameComponent, currentAction, id }: ITimeActionDetail) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const hasAllOrdersinTimeRegistration = useFeature('orders-in-time-registration')

  const currentEmployee = useSelector<IRootReducerState, IEmployee>(state => state.auth.employee)

  const {
    data: result,
    isLoading,
    refetch: reload
  } = useQuery(
    ['ToggleClockIn', item.UUID],
    async () => {
      const [currentResponse, workorderClocksResponse] = await Promise.all([
        dispatch(currentClockingAction()),
        dispatch(workorderClockingActions(item.UUID))
      ])

      return {
        currentAction: currentResponse.Data,
        workorderClockedHours: clockResponseToTimePerEmployee(
          workorderClocksResponse.Data,
          currentResponse.Data.ServerTime,
          currentEmployee.UUID
        )
      }
    },
    { enabled: id === 'workorders' }
  )

  const isCurrentAction = !!currentAction && (currentAction.ClockingItemUUID === item.UUID || currentAction.WorkorderUUID === item.UUID)

  const totalTime = useMemo(() => {
    if (!result) {
      return {
        start: '',
        totalTime: moment.duration(),
        isActive: false
      }
    }

    const time = moment.duration()
    result.workorderClockedHours.forEach(item => time.add(item.totalTime))

    return {
      start: moment(result.currentAction.ServerTime, apiFormat.iso).subtract(time).toISOString(),
      totalTime: time,
      isActive: result.workorderClockedHours.some(clockingAction => clockingAction.isActive)
    }
  }, [result, isCurrentAction])

  return (
    <Table.Row key={item.UUID}>
      <Table.Cell
        collapsing
        className='text-overflow-hidden'
        style={{ maxWidth: '200px', borderTop: 0, borderBottom: '1px solid #2224261a' }}
      >
        {NameComponent ? (
          <NameComponent name={item.Name} url={item.UrlToPage} brand={item.Brand} />
        ) : (
          <Tooltip
            title={item.Name}
            content={item.Name}
            arrow
            className='text-overflow-hidden'
            disableInteractive
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -10]
                  }
                }
              ]
            }}
          >
            <Typography variant='body1'>{item.Name}</Typography>
          </Tooltip>
        )}
      </Table.Cell>
      <Table.Cell textAlign='center' style={{ minWidth: '107px', borderTop: 0, borderBottom: '1px solid #2224261a' }}>
        {isCurrentAction ? (
          id === 'items' ? (
            <Timer workedTime={currentAction!.TotalClockedSeconds} isInActions />
          ) : isLoading ? (
            <Loader loading />
          ) : (
            <Timer workedTime={totalTime.totalTime.asSeconds()} isInActions />
          )
        ) : id === 'workorders' ? (
          !isLoading ? (
            <>
              <Icon name='clock outline' />
              {moment.duration(totalTime.totalTime, 's').format('HH:mm:ss', { trim: false })}
            </>
          ) : (
            <Loader loading />
          )
        ) : null}
      </Table.Cell>
      <Table.Cell
        collapsing
        data-testid={`timeregistration-actions-${id}-toggle`}
        onClick={() => {
          id === 'workorders' && reload()
          isCurrentAction ? clockOut() : clockIn(item.UUID)
        }}
        style={{
          marginRight: 0,
          borderTop: 0,
          borderBottom: '1px solid #2224261a'
        }}
      >
        {hasAllOrdersinTimeRegistration ? (
          isCurrentAction ? (
            <Button size='small' fullWidth variant='outlined' startIcon={<StopIcon fontSize='small' />} sx={{ pt: 0.625 }}>
              {t('TimeRegistration.Stop')}
            </Button>
          ) : (
            <Button size='small' fullWidth variant='outlined' startIcon={<PlayArrowIcon fontSize='small' />} sx={{ pt: 0.625 }}>
              {t('TimeRegistration.Start')}
            </Button>
          )
        ) : isCurrentAction ? (
          <SUIButton
            size='tiny'
            style={{
              marginRight: 0
            }}
          >
            <Icon name='stop' />
            {t('TimeRegistration.Stop')}
          </SUIButton>
        ) : (
          <SUIButton
            size='tiny'
            style={{
              marginRight: 0
            }}
          >
            <Icon name='play' />
            {t('TimeRegistration.Start')}
          </SUIButton>
        )}
      </Table.Cell>
    </Table.Row>
  )
}

export default TimeActionDetails
